import { useState, type ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import clsx from "clsx";
import { ArrowRight, Briefcase, Building2, CalendarClock, Check, Factory, HardHat, Loader2, Shapes, Store, Truck, Users, type LucideIcon } from "lucide-react";
import { api, type S } from "@/shared/api/client";
import { errorMessage, joinList } from "@/shared/i18n";
import { meQuery, useCan } from "@/shared/lib/session";
import { ConfirmDialog } from "@/shared/ui/Modal";
import { ErrorBox, Section } from "@/shared/ui/Page";
import { Wordmark } from "@/shared/ui/Wordmark";
import { choosable } from "./apps";
import { useAppStart } from "./AppStart";
import { DemoButton, DemoSettings } from "./Demo";
import { BUSINESSES, recommendedApps, TEAM_SIZES, type Business, type TeamSize } from "./recommend";
import { CompanyDetails, noCompanyAnswers, saveCompanyAnswers, type CompanyAnswers } from "./WelcomeCompany";

/** What each app can't be on without, as the server declares it (and enforces). */
export function useAppNeeds(): Record<string, string[]> {
  const { data: me } = useQuery(meQuery);
  return Object.fromEntries(Object.entries(me?.tenant.app_needs ?? {}).map(([k, v]) => [k, v ?? []]));
}

/** Selecting an app selects what it needs; unselecting one unselects the apps that need it. */
export function toggleApp(selected: string[], key: string, needs: Record<string, string[]>): string[] {
  if (selected.includes(key)) {
    return selected.filter((k) => k !== key && !needs[k]?.includes(key));
  }
  const add = new Set([...selected, key, ...(needs[key] ?? [])]);
  return choosable.map((a) => a.key).filter((k) => add.has(k));
}

/** Saves the apps switched on, then reloads the profile so menus, guards, "start here" and what can be imported follow at once. */
export function useSaveApps() {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const save = async (apps: string[], answers?: { business?: Business; team_size?: TeamSize }) => {
    setBusy(true);
    try {
      await api.put("/v1/core/apps", { apps, ...answers });
      await qc.fetchQuery({ ...meQuery, staleTime: 0 });
      await qc.invalidateQueries({ queryKey: ["/v1/core/setup"] });
      await qc.invalidateQueries({ queryKey: ["/v1/core/imports"] });
    } finally {
      setBusy(false);
    }
  };
  return { save, busy };
}

/** The apps as cards to switch on and off. */
export function AppCards({ selected, onToggle, busy, className }: { selected: string[]; onToggle: (key: string) => void; busy?: boolean; className?: string }) {
  const { t } = useTranslation();
  const { data: me } = useQuery(meQuery);
  const needs = useAppNeeds();
  const plan = me?.tenant.plan_modules ?? [];
  // What's on is what the menu shows: the price is the plan's, not an app's.
  return (
    <ul className={className ?? "grid gap-3 sm:grid-cols-2 lg:grid-cols-3"}>
      {choosable.map((a) => {
        const on = selected.includes(a.key);
        const inPlan = plan.includes(a.key);
        return (
          <li key={a.key}>
            <button type="button" role="checkbox" aria-checked={on} disabled={!inPlan || busy} onClick={() => onToggle(a.key)}
              className={clsx("panel flex h-full w-full items-start gap-3 p-4 text-start transition-colors disabled:cursor-not-allowed",
                on ? "border-ledger bg-ledger-soft/60" : "hover:border-ink-3", !inPlan && "opacity-60")}>
              <span className={clsx("flex size-9 shrink-0 items-center justify-center rounded-[var(--radius-ctl)]",
                on ? "bg-ledger text-white" : "bg-line-2 text-ink-2")}>
                <a.icon className="size-5" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center justify-between gap-2 font-semibold">
                  {t(`apps.${a.key}.name`)}
                  {on && <Check className="size-4 shrink-0 text-ledger" aria-hidden />}
                </span>
                <span className="mt-0.5 block text-[13px] text-ink-2">{t(`apps.${a.key}.description`)}</span>
                {!inPlan ? <span className="mt-1 block text-[12px] text-ink-3">{t("apps.notInPlan")}</span>
                  : needs[a.key]?.length ? (
                    <span className="mt-1 block text-[12px] text-ink-3">
                      {t("apps.needs", { app: joinList(needs[a.key].map((r) => t(`apps.${r}.name`))) })}
                    </span>
                  ) : null}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

const businessIcons: Record<Business, LucideIcon> = {
  trading: Truck, retail: Store, services: Briefcase, appointments: CalendarClock,
  contracting: HardHat, realestate: Building2, manufacturing: Factory, other: Shapes,
};

/** One answer of several, as a card or a pill. */
function Choice({ on, onClick, className, children }: { on: boolean; onClick: () => void; className?: string; children: ReactNode }) {
  return (
    <button type="button" role="radio" aria-checked={on} onClick={onClick}
      className={clsx("panel text-start transition-colors", on ? "border-ledger bg-ledger-soft/60" : "hover:border-ink-3", className)}>
      {children}
    </button>
  );
}

/**
 * First sign-in of the owner, one question at a time: what the company does, how many work
 * there, the apps it starts with (suggested from those two, and still picked one by one),
 * and the details its documents carry. Every step but the apps can be skipped.
 */
export function WelcomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const can = useCan();
  const { data: me } = useQuery(meQuery);
  const [business, setBusiness] = useState<Business>();
  const [team, setTeam] = useState<TeamSize>();
  const [selected, setSelected] = useState<string[]>([]);
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const { save } = useSaveApps();
  const needs = useAppNeeds();
  const [err, setErr] = useState<unknown>(null);
  const plan = me?.tenant.plan_modules ?? [];
  const companyId = me?.current_company_id;
  const company = useQuery({ queryKey: ["/v1/core/companies", companyId], queryFn: () => api.get<S["Company"]>(`/v1/core/companies/${companyId}`), enabled: !!companyId });
  // What to suggest, as the back-office set it (the app's own suggestions until it answers).
  const status = useQuery({ queryKey: ["platform-status"], queryFn: () => api.get<S["PlatformStatus"]>("/v1/platform/status") });
  const [details, setDetails] = useState<CompanyAnswers>(noCompanyAnswers);
  const total = company.data && can("core.company.update") ? 4 : 3;
  const last = step === total;
  const answer = (b: Business | undefined, s: TeamSize | undefined) => {
    setBusiness(b);
    setTeam(s);
    if (b) setSelected(recommendedApps(b, s, choosable.map((a) => a.key).filter((k) => plan.includes(k)), status.data?.onboarding ?? undefined));
  };
  const start = async (withDetails: boolean) => {
    setErr(null);
    setBusy(true);
    try {
      if (withDetails && company.data) await saveCompanyAnswers(company.data, details);
      // The route guard reads the cached profile, which save has reloaded.
      await save(selected, { business, team_size: team });
      await navigate({ to: "/" });
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };
  const next = () => (last ? start(true) : setStep(step + 1));
  const title = [t("welcome.businessQuestion"), t("welcome.teamQuestion"), t("welcome.question"), t("welcome.company.title")][step - 1];
  const lead = [t("welcome.lead"), t("welcome.teamLead"), business ? t("welcome.suggested") : t("welcome.hint"), t("welcome.company.lead")][step - 1];
  const blocked = busy || (step === 3 && selected.length === 0) || (step === 4 && details.vat === true && !details.tax_number.trim());
  const canSkip = step !== 3;
  return (
    <div className="flex min-h-dvh flex-col bg-paper">
      <header className="flex items-center justify-between px-6 py-5 sm:px-10">
        <Wordmark className="h-6 w-auto" />
        <span className="text-[13px] text-ink-3" aria-live="polite">{t("welcome.step", { n: step, total })}</span>
      </header>
      <div className="mx-6 flex gap-1.5 sm:mx-10" aria-hidden>
        {Array.from({ length: total }, (_, k) => <span key={k} className={clsx("h-1 flex-1 rounded-full transition-colors duration-300", k < step ? "bg-ledger" : "bg-line")} />)}
      </div>
      <main className="mx-auto w-full max-w-[680px] flex-1 px-6 pt-10 pb-8">
        <div key={step} className="step-in">
          {step === 1 && <p className="mb-2 text-[14px] font-medium text-ledger">{t("welcome.greeting", { name: me?.name.split(" ")[0] ?? "" })}</p>}
          <h1 className="text-[28px] leading-tight font-bold tracking-tight">{title}</h1>
          <p className="mt-2 max-w-xl text-ink-2">{lead}</p>
          {err != null && <div className="mt-4"><ErrorBox error={err} /></div>}
          <div className="mt-7">
            {step === 1 && (
              <div role="radiogroup" aria-label={title} className="grid gap-3 sm:grid-cols-2">
                {BUSINESSES.map((b) => {
                  const Icon = businessIcons[b];
                  return (
                    <Choice key={b} on={business === b} onClick={() => answer(b, team)} className="flex items-start gap-3 p-4">
                      <span className={clsx("flex size-10 shrink-0 items-center justify-center rounded-[var(--radius-ctl)]", business === b ? "bg-ledger text-white" : "bg-line-2 text-ink-2")}>
                        <Icon className="size-5" aria-hidden />
                      </span>
                      <span className="min-w-0">
                        <span className="block font-semibold">{t(`welcome.businesses.${b}.name`)}</span>
                        <span className="mt-0.5 block text-[13px] text-ink-2">{t(`welcome.businesses.${b}.hint`)}</span>
                      </span>
                    </Choice>
                  );
                })}
              </div>
            )}
            {step === 2 && (
              <div role="radiogroup" aria-label={title} className="grid gap-3 sm:grid-cols-2">
                {TEAM_SIZES.map((s) => (
                  <Choice key={s} on={team === s} onClick={() => answer(business, s)} className="flex items-center gap-3 p-4 text-[16px] font-semibold">
                    <span className={clsx("flex size-10 shrink-0 items-center justify-center rounded-full", team === s ? "bg-ledger text-white" : "bg-line-2 text-ink-2")}><Users className="size-5" aria-hidden /></span>
                    {t(`welcome.teams.${s}`)}
                  </Choice>
                ))}
              </div>
            )}
            {step === 3 && <AppCards selected={selected} onToggle={(k) => setSelected((x) => toggleApp(x, k, needs))} busy={busy} className="grid gap-3 sm:grid-cols-2" />}
            {step === 4 && company.data && <CompanyDetails company={company.data} value={details} onChange={setDetails} />}
          </div>
        </div>
      </main>
      <footer className="sticky bottom-0 border-t border-line bg-surface/95 backdrop-blur">
        <div className="mx-auto flex w-full max-w-[680px] flex-wrap items-center gap-x-4 gap-y-2 px-6 py-4">
          {step > 1 ? (
            <button type="button" className="btn-ghost" onClick={() => setStep(step - 1)} disabled={busy}><ArrowRight className="size-4 ltr:rotate-180" aria-hidden /> {t("welcome.back")}</button>
          ) : (
            <p className="min-w-0 flex-1 basis-60 text-[13px] text-ink-2"><DemoButton className="text-ledger" label={t("demo.offer")} /> {t("demo.offerHint")}</p>
          )}
          <span className="flex-1" />
          {canSkip && <button type="button" className="btn-ghost" disabled={busy} onClick={() => (last ? start(false) : setStep(step + 1))}>{t("welcome.skip")}</button>}
          <button className="btn-primary px-8 py-2.5" disabled={blocked} onClick={next}>
            {busy && <Loader2 className="size-4 animate-spin" />} {last ? t("welcome.start") : t("welcome.next")}
          </button>
        </div>
      </footer>
    </div>
  );
}

/** Settings → Apps: switch apps on and off; data stays. Switching off asks first, naming the
 * apps that go with it and what each stops doing. */
export function AppsSettings() {
  const { t } = useTranslation();
  const { data: me } = useQuery(meQuery);
  const { save, busy } = useSaveApps();
  const needs = useAppNeeds();
  const start = useAppStart();
  const [off, setOff] = useState<string[] | null>(null);
  const selected = (me?.tenant.modules ?? []).filter((m) => m !== "core");
  // Switched on, the app says where it is and what to do first.
  const apply = async (next: string[], on?: string) => {
    try {
      await save(next);
      setOff(null);
      toast.success(t("common.saved"));
      if (on) start(on);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  const toggle = (key: string) => {
    const next = toggleApp(selected, key, needs);
    if (next.length === 0) {
      toast.error(t("apps.lastOne"));
      return;
    }
    if (selected.includes(key)) setOff(next);
    else void apply(next, key);
  };
  const going = off ? selected.filter((k) => !off.includes(k)) : [];
  return (
    <div className="space-y-6">
      <Section title={t("apps.title")}>
        <p className="mb-4 max-w-2xl text-ink-2">{t("apps.hint")}</p>
        <AppCards selected={selected} onToggle={toggle} busy={busy} />
      </Section>
      <DemoSettings />
      <ConfirmDialog open={off != null} onClose={() => setOff(null)} danger busy={busy}
        title={t("apps.offConfirm", { apps: joinList(going.map((k) => t(`apps.${k}.name`))) })}
        message={t("apps.offKept")} confirmLabel={t("apps.offButton")} onConfirm={() => off && apply(off)}>
        <ul className="mt-3 space-y-2 text-[14px]">
          {going.map((k) => (
            <li key={k}><span className="font-medium">{t(`apps.${k}.name`)}:</span> {t(`apps.stops.${k}`)}</li>
          ))}
        </ul>
      </ConfirmDialog>
    </div>
  );
}
